import * as React from 'react'
import { motion } from 'framer-motion'
import { cn } from '../../utils/cn'
import { Crest } from '../display/crest'

/**
 * JPG Academy Empty State
 *
 * A consistent "no data" display with the school crest at low opacity.
 * Feels intentional, not broken — like a clean sheet of parchment.
 *
 * Variants:
 *   - Default (no icon, no showCrest): Uses crest automatically at low opacity
 *   - showCrest: Explicitly show the crest
 *   - icon: Show a custom icon inside a cream circle
 *   - compact: Smaller padding and text for use inside cards
 *
 * Motion is built in and respects prefers-reduced-motion.
 */

export interface EmptyStateProps extends React.HTMLAttributes<HTMLDivElement> {
	icon?: React.ReactNode
	showCrest?: boolean
	crestSize?: 'sm' | 'md' | 'lg'
	title: string
	description?: string
	action?: React.ReactNode
	compact?: boolean
}

export function EmptyState({ icon, showCrest = false, crestSize = 'md', title, description, action, compact = false, className, ...props }: EmptyStateProps) {
	const useCrest = showCrest || (!icon && !showCrest)
	const crestSizeMap = compact ? 'sm' : crestSize

	const iconContent = useCrest ? (
		<div className="opacity-20"><Crest size={crestSizeMap} /></div>
	) : icon ? (
		<div className={cn('rounded-full bg-cream flex items-center justify-center', compact ? 'w-10 h-10 text-navy/30' : 'w-14 h-14 text-navy/30')}>
			{icon}
		</div>
	) : (
		<div className="opacity-20"><Crest size={compact ? 'sm' : 'md'} /></div>
	)

	return (
		<div
			className={cn(
				'flex flex-col items-center text-center rounded-lg border border-dashed border-navy-100',
				compact ? 'py-6 px-4' : 'py-12 px-6',
				className,
			)}
			{...props}
		>
			<motion.div className={cn('flex items-center justify-center', compact ? 'mb-3' : 'mb-4')} initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4, ease: [0, 0, 0.2, 1] }}>
				{iconContent}
			</motion.div>
			<motion.h3 className={cn('font-body font-semibold text-navy', compact ? 'text-sm' : 'text-base')} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.1, ease: [0.4, 0, 0.2, 1] }}>
				{title}
			</motion.h3>
			{description && <motion.p className={cn('text-navy/50 font-ui mt-1 max-w-sm', compact ? 'text-xs' : 'text-sm')} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.15, ease: [0.4, 0, 0.2, 1] }}>{description}</motion.p>}
			{action && <motion.div className={cn(compact ? 'mt-3' : 'mt-4')} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3, delay: 0.2, ease: [0.4, 0, 0.2, 1] }}>{action}</motion.div>}
		</div>
	)
}
